import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { SUPPORTED_LOCALES, TRANSLATABLE_LOCALES } from './supportedLocales';

import esCommon from './locales/es/common.json';
import enCommon from './locales/en/common.json';
import esHome from './locales/es/home.json';
import enHome from './locales/en/home.json';
import esProduct from './locales/es/product.json';
import enProduct from './locales/en/product.json';
import esCart from './locales/es/cart.json';
import enCart from './locales/en/cart.json';
import esCheckout from './locales/es/checkout.json';
import enCheckout from './locales/en/checkout.json';
import esAccount from './locales/es/account.json';
import enAccount from './locales/en/account.json';
import esArtist from './locales/es/artist.json';
import enArtist from './locales/en/artist.json';
import esTerms from './locales/es/terms.json';
import enTerms from './locales/en/terms.json';
import esMailing from './locales/es/mailing.json';
import enMailing from './locales/en/mailing.json';
import esTtf from './locales/es/ttf.json';

const NAMESPACES = ['common', 'home', 'product', 'cart', 'checkout', 'account', 'artist', 'terms', 'mailing'];

// Spanish only (the Taipei Toy Festival page) — not in the en/zh/ja
// resources or barrels, so every locale falls back to Spanish for it.
const ES_ONLY_NAMESPACES = ['ttf'];

// Locales loaded eagerly at boot vs. on demand. es is the default and en is
// the next most-used (opt-in toggle right next to es) — both are cheap
// enough, and common enough, to always bundle. zh/ja are lazy: see
// loadLocale() below.
const EAGER_LOCALES = ['es', 'en'];
const LAZY_LOCALES = TRANSLATABLE_LOCALES.filter((l) => !EAGER_LOCALES.includes(l));

const loadedLazyLocales = new Set();

// One dynamic import() per lazy locale (not per namespace) — each barrel
// file (src/i18n/locales/<lang>/index.js) re-exports all 8 namespaces, so
// switching to zh/ja costs one chunk request instead of eight.
const LAZY_LOCALE_LOADERS = {
  zh: () => import('./locales/zh/index.js'),
  ja: () => import('./locales/ja/index.js'),
};

/**
 * Fetch and register a lazily-loaded locale's namespace JSON, if it hasn't
 * been already. No-op for eager locales (es/en) or an already-loaded locale.
 *
 * Dynamic `import()` here — rather than the static imports above — keeps
 * the ~44KB of zh strings and ~44KB of ja strings out of every visitor's
 * initial bundle; the overwhelming majority of shoppers never switch away
 * from es/en. i18next's `fallbackLng: 'es'` renders the Spanish string for
 * any key not yet loaded, so there's no blank/broken flash while the chunk
 * downloads — components using `useTranslation()` re-render automatically
 * once `addResourceBundle()` fires below (react-i18next subscribes to the
 * `loaded` event by default, no Suspense needed).
 */
export async function loadLocale(lang) {
  if (!LAZY_LOCALES.includes(lang) || loadedLazyLocales.has(lang)) return;
  loadedLazyLocales.add(lang);

  const mod = await LAZY_LOCALE_LOADERS[lang]();
  NAMESPACES.forEach((ns) => {
    i18n.addResourceBundle(lang, ns, mod[ns], true, true);
  });
}

/**
 * i18next configuration.
 *
 * Default locale is Spanish — Bluppimart is targeted at Mexican collectors.
 * English/Chinese/Japanese are opt-in via the LanguageSwitcher in the Header.
 *
 * Persistence: the chosen language is saved to localStorage under
 * `bluppimart.lang` so it survives page reloads. We deliberately do NOT
 * detect from `navigator.language` because the brand defaults to ES.
 *
 * Namespaces: split by area (common, home, product, cart, checkout,
 * account, artist, terms). es/en are bundled at boot; zh/ja are lazy — see
 * loadLocale() above.
 *
 * zh/ja cover both tiers: UI strings here, and database content
 * (product/artist names, descriptions, bios) via the `_zh`/`_ja` columns
 * added in migrations/063_zh_ja_translation_columns.sql. tField()
 * (src/utils/i18nContent.js) reads those columns directly and falls back
 * to the Spanish canonical value only when a given row has no translation
 * yet — same fallback behavior as untranslated `_en` fields.
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'es',
    supportedLngs: SUPPORTED_LOCALES,
    defaultNS: 'common',
    ns: [...NAMESPACES, ...ES_ONLY_NAMESPACES],
    resources: {
      es: { common: esCommon, home: esHome, product: esProduct, cart: esCart, checkout: esCheckout, account: esAccount, artist: esArtist, terms: esTerms, mailing: esMailing, ttf: esTtf },
      en: { common: enCommon, home: enHome, product: enProduct, cart: enCart, checkout: enCheckout, account: enAccount, artist: enArtist, terms: enTerms, mailing: enMailing },
    },
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: 'bluppimart.lang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    returnNull: false,
  });

// A returning visitor may have zh/ja persisted from a previous session —
// load it proactively rather than waiting for a LanguageSwitcher click, so
// the brief Spanish-fallback flash (see loadLocale() above) is as short as
// possible.
if (LAZY_LOCALES.includes(i18n.resolvedLanguage)) {
  loadLocale(i18n.resolvedLanguage);
}

export default i18n;
